import { useEffect, useState } from "react";
import { FaQuestionCircle, FaPencilAlt } from "react-icons/fa";

type QuizExerciseSideBarProps = {
  courseTitle: string;
  lessonTitle: string;
  subLessonTitle: string;
  items: any[];
  selectedItem: any | null;
  onSelectItem: (item: any) => void;
  onCourseClick: () => void;
  onLessonClick: () => void;
  onSubLessonClick: () => void;
  mode: "quiz" | "exercise";
};

export const QuizExerciseSideBar = ({
  courseTitle,
  lessonTitle,
  subLessonTitle,
  items,
  selectedItem,
  onSelectItem,
  onCourseClick,
  onLessonClick,
  onSubLessonClick,
  mode,
}: QuizExerciseSideBarProps) => {
  const isQuiz = mode === "quiz";
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => document.body.classList.remove("overflow-hidden");
  }, [isSidebarOpen]);

  return (
    <>
      <div className="md:h-[calc(100vh-200px)] thin-scrollbar overflow-y-auto overflow-x-hidden md:border-r border-[#D0D0D0] md:w-52 md:min-w-[13rem] md:max-w-[13rem] max-md:w-full flex-shrink-0 break-words">
        <div className="mb-4 flex flex-wrap text-sm gap-1 max-md:hidden">
          <div
            className="font-semibold cursor-pointer flex items-center gap-1"
            onClick={() => { onCourseClick() }}
          >
            <span className="text-primary hover:underline">{courseTitle}</span> <span className="text-black">&gt;</span>
          </div>
          <div
            className=" font-semibold cursor-pointer flex items-center gap-1"
            onClick={() => { onLessonClick() }}
          >
            <span className="text-primary hover:underline">{lessonTitle} </span> <span className="text-black">&gt;</span>
          </div>
          <div
            className="text-primary font-semibold cursor-pointer hover:underline flex items-center gap-1"
            onClick={() => { onSubLessonClick() }}
          >
            {subLessonTitle}
          </div>
        </div>

        {subLessonTitle && (
          <div className="text-gray-600 font-medium mb-3 max-md:hidden">{subLessonTitle}</div>
        )}

        {items.length > 0 && (
          <div className="max-md:hidden">
            <div className="text-gray-500 font-semibold mb-2">
              {isQuiz ? "Quizzes" : "Exercises"}
            </div>

            {items.map((item: any, index: number) => {
              const isActive = selectedItem?.id === item.id;
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 cursor-pointer
                  ${isActive ? "text-primary font-semibold" : "text-gray-700 hover:text-primary hover:font-semibold"}
                  border-t border-gray-200 pt-2
                  ${index === items.length - 1 ? "border-b border-gray-200 pb-2" : "pb-2"}
                `}
                  onClick={() => {
                    onSelectItem(item)
                  }}
                >
                  {isQuiz ? (
                    <FaQuestionCircle className="text-lg flex-shrink-0" />
                  ) : (
                    <FaPencilAlt className="text-lg flex-shrink-0" />
                  )}
                  <span className=" flex-1 text-sm font-semibold break-words whitespace-normal leading-snug">{item.title}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="w-full p-3 border-b border-[#D0D0D0] md:hidden flex justify-end items-center">

          {/* Mobile Menu Button */}
          <button onClick={() => setSidebarOpen(true)}>
            <span className="material-symbols-outlined text-3xl">menu</span>
          </button>
        </div>
      </div>
      {isSidebarOpen && (
        <div className="fixed inset-0 z-[10000] flex">

          {/* BACKDROP */}
          <div
            className="fixed inset-0 bg-black bg-opacity-40"
            onClick={() => setSidebarOpen(false)}
          />

          {/* SLIDING DRAWER */}
          <div className="relative z-[10001] w-72 bg-white h-screen shadow-xl animate-slideIn pb-12">

            {/* Header */}
            <div className="flex items-center justify-end p-4 border-b">

              <button onClick={() => setSidebarOpen(false)}>
                <span className="material-symbols-outlined text-3xl">close</span>
              </button>
            </div>

            {/* Lesson Items */}
            <div className="overflow-y-auto max-md:h-[calc(100vh-200px)] p-2">
              <div className="mb-4 flex flex-wrap text-sm gap-1">
                <div
                  className="font-semibold cursor-pointer flex items-center gap-1"
                  onClick={() => {
                    setSidebarOpen(false);
                    onCourseClick()
                  }}
                >
                  <span className="text-primary hover:underline">{courseTitle}</span> <span className="text-black">&gt;</span>
                </div>
                <div
                  className=" font-semibold cursor-pointer flex items-center gap-1"
                  onClick={() => {
                    setSidebarOpen(false);
                    onLessonClick()
                  }}
                >
                  <span className="text-primary hover:underline">{lessonTitle} </span> <span className="text-black">&gt;</span>
                </div>
                <div
                  className="text-primary font-semibold cursor-pointer hover:underline flex items-center gap-1"
                  onClick={() => {
                    setSidebarOpen(false);
                    onSubLessonClick()
                  }}
                >
                  {subLessonTitle}
                </div>
              </div>

              {subLessonTitle && (
                <div className="text-gray-600 font-medium mb-3 ">{subLessonTitle}</div>
              )}

              {items.length > 0 && (
                <div>
                  <div className="text-gray-500 font-semibold mb-2">
                    {isQuiz ? "Quizzes" : "Exercises"}
                  </div>

                  {items.map((item: any, index: number) => {
                    const isActive = selectedItem?.id === item.id;
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 cursor-pointer
                  ${isActive ? "text-primary font-semibold" : "text-gray-700 hover:text-primary hover:font-semibold"}
                  border-t border-gray-200 pt-2
                  ${index === items.length - 1 ? "border-b border-gray-200 pb-2" : "pb-2"}
                `}
                        onClick={() => {
                          setSidebarOpen(false);
                          onSelectItem(item)
                        }}
                      >
                        {isQuiz ? (
                          <FaQuestionCircle className="text-lg flex-shrink-0" />
                        ) : (
                          <FaPencilAlt className="text-lg flex-shrink-0" />
                        )}
                        <span className="text-sm leading-snug break-words">{item.title}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
